import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, RefreshCw, Award, BarChart2, List } from 'lucide-react';
import { Container, Row, Col } from 'react-bootstrap';
import SideNav from '../components/SideNav';

const getCurrentMonthYear = () => {
  const now = new Date();
  return {
    month: now.toLocaleString('en-US', { month: 'long' }),
    year: now.getFullYear().toString(),
  };
};

const apiService = {
  getCurrentMonthYear: () => {
    const now = new Date();
    return {
      year: now.getFullYear().toString(),
      month: now.toLocaleString('default', { month: 'long' }),
    };
  },

  buildQueryParams: (filters = {}) => {
    const qp = new URLSearchParams();
    const { year: cy, month: cm } = apiService.getCurrentMonthYear();
    const merged = {
      year: filters.year || [{ value: cy, label: cy }],
      month: filters.month || [{ value: cm, label: cm }],
      ...filters,
    };
    const skip = new Set(['allYears', 'allMonths', 'allCities', 'allReps', 'allSources']);
    Object.entries(merged).forEach(([key, vals]) => {
      if (Array.isArray(vals) && vals.length > 0) {
        const strs = vals
          .map(i => (typeof i === 'object' && i.value ? i.value : i))
          .filter(v => !skip.has(v));
        if (strs.length) qp.append(key, strs.join(','));
      }
    });
    return qp;
  },

  fetchSalesData: async (filters = {}) => {
    const qp = apiService.buildQueryParams(filters);
    const res = await fetch(`https://api-4h4srbodqa-uc.a.run.app/api/sales-data?${qp}`);
    if (!res.ok) throw new Error('Failed to fetch sales data');
    return res.json();
  },

  fetchFilterOptions: async () => {
    const res = await fetch('https://api-4h4srbodqa-uc.a.run.app/api/filter-options');
    if (!res.ok) throw new Error('Failed to fetch filter options');
    return res.json();
  },
};

const BAR_COLORS = [
  '#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#06b6d4', '#f97316', '#84cc16', '#ec4899',
];
const getColor = (idx) => BAR_COLORS[idx % BAR_COLORS.length];

const RacingTrackBoard = () => {
  const { month: currentMonth, year: currentYear } = getCurrentMonthYear();

  const [selectedYears] = useState([{ value: currentYear, label: currentYear }]);
  const [selectedMonths] = useState([{ value: currentMonth, label: currentMonth }]);
  const [selectedCities] = useState([]);
  const [selectedSalesReps] = useState([]);
  const [animationProgress, setAnimationProgress] = useState(0);
  // Mobile tab: 'chart' | 'table'
  const [mobileTab, setMobileTab] = useState('chart');

  const { data: filterOptions, isLoading: isLoadingFilters } = useQuery({
    queryKey: ['filterOptions'],
    queryFn: apiService.fetchFilterOptions,
    staleTime: 5 * 60 * 1000,
  });

  const { data: salesData, isLoading: isLoadingSales, refetch } = useQuery({
    queryKey: ['salesData', selectedYears, selectedMonths, selectedCities, selectedSalesReps],
    queryFn: () =>
      apiService.fetchSalesData({
        year: selectedYears,
        month: selectedMonths,
        city: selectedCities,
        salesRep: selectedSalesReps,
      }),
    enabled: true,
  });

  useEffect(() => {
    if (animationProgress >= 100) return;
    const t = setTimeout(() => setAnimationProgress(p => Math.min(p + 2, 100)), 20);
    return () => clearTimeout(t);
  }, [animationProgress]);

  const salesPerRep = useMemo(() => {
    if (!salesData?.data?.length) return [];
    const map = {};
    salesData.data.forEach(item => {
      const rep = item.salesRep || 'Unknown';
      const amt = parseFloat(item.closingAmount) || 0;
      if (map[rep]) { map[rep].total += amt; map[rep].count += 1; }
      else { map[rep] = { total: amt, count: 1 }; }
    });
    return Object.entries(map)
      .map(([rep, d]) => ({ rep, total: d.total, count: d.count }))
      .sort((a, b) => b.total - a.total);
  }, [salesData]);

  const topRep = salesPerRep[0] ?? null;
  const topTotal = topRep?.total ?? 1;

  if (isLoadingFilters) {
    return (
      <Container fluid className="p-3" style={{ backgroundColor: '#F9FAFB', height: '100vh' }}>
        <Row>
          <Col className="col-auto"><SideNav /></Col>
          <Col className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
            <Loader2 className="animate-spin" size={48} />
          </Col>
        </Row>
      </Container>
    );
  }

  const LeaderboardChart = () => (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 className="card-title mb-0">Leaderboard</h5>
        {topRep && (
          <span className="badge bg-warning text-dark d-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
            <Award size={13} />
            <span className="d-none d-sm-inline">Leader: </span>
            {topRep.rep} — ₹{(topRep.total / 100000).toFixed(1)}L
          </span>
        )}
      </div>

      <div className="card-body" style={{ maxHeight: 640, overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {salesPerRep.map((person, idx) => {
            const barColor = getColor(idx);
            const rawPct = (person.total / topTotal) * 100;
            const animPct = (rawPct * animationProgress) / 100;

            return (
              <div key={person.rep} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                {/* Name — truncated on small screens */}
                <div style={{
                  width: 'clamp(80px, 22%, 130px)',
                  fontSize: 'clamp(11px, 2vw, 13px)',
                  fontWeight: 600,
                  textAlign: 'right',
                  color: '#374151',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 4,
                  overflow: 'hidden',
                }}>
                  {idx === 0 && <span style={{ fontSize: 14, flexShrink: 0 }}>🏆</span>}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {person.rep}
                  </span>
                </div>

                {/* Bar track */}
                <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                  <div style={{
                    height: 36,
                    backgroundColor: '#f3f4f6',
                    borderRadius: 6,
                    overflow: 'visible',
                    position: 'relative',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${animPct}%`,
                      borderRadius: 6,
                      background: `linear-gradient(90deg, ${barColor}cc, ${barColor})`,
                      transition: 'width 0.25s ease-out',
                      position: 'relative',
                      zIndex: 1,
                    }} />
                    {/* Avatar bubble */}
                    <div style={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '50%',
                      left: `${animPct}%`,
                      transform: 'translate(-50%, -50%)',
                      transition: 'left 0.25s ease-out',
                      backgroundColor: '#fff',
                      border: `2.5px solid ${barColor}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 12,
                      color: barColor,
                      boxShadow: `0 2px 8px ${barColor}55`,
                      zIndex: 2,
                    }}>
                      {person.rep.charAt(0).toUpperCase()}
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div style={{ width: 'clamp(60px, 15%, 115px)', textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 'clamp(12px, 2vw, 14px)', fontWeight: 700, color: '#111827' }}>
                    ₹{(person.total / 100000).toFixed(1)}L
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const PerformanceTable = () => (
    <div className="card h-100">
      <div className="card-header">
        <h5 className="card-title mb-0">Detailed Performance</h5>
      </div>
      <div className="card-body p-0">
        <div className="table-responsive" style={{ maxHeight: 500, overflowY: 'auto' }}>
          <table className="table table-hover mb-0">
            <thead className="table-light sticky-top">
              <tr>
                <th>Rank</th>
                <th>Sales Rep</th>
                <th>Total Sales</th>
                <th>Deals</th>
              </tr>
            </thead>
            <tbody>
              {salesPerRep.map((rep, i) => {
                const color = getColor(i);
                return (
                  <tr key={rep.rep}>
                    <td>
                      {i === 0
                        ? <span style={{ fontSize: 16 }}>🏆</span>
                        : <span className="text-muted">#{i + 1}</span>}
                    </td>
                    <td className="fw-medium" style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {rep.rep}
                    </td>
                    <td>
                      <span className="fw-semibold" style={{ color }}>
                        ₹{rep.total.toLocaleString('en-IN')}
                      </span>
                    </td>
                    <td>{rep.count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <Container fluid className="p-2 p-md-3" style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <Row className="g-0">
        <Col className="col-auto"><SideNav /></Col>

        <Col style={{ padding: '0 8px 0 12px', minWidth: 0, flexGrow: 1 }}>
          <Row className="p-2 p-md-3" style={{ backgroundColor: '#fff', borderRadius: 10, marginBottom: 16 }}>

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div>
                <h1
                  className="fw-bold text-dark mb-0"
                  style={{ fontSize: 'clamp(1.2rem, 4vw, 1.75rem)' }}
                >
                  Champions Board
                </h1>
                <p className="text-muted mb-0" style={{ fontSize: 'clamp(0.8rem, 2.5vw, 1rem)' }}>
                  {currentMonth} {currentYear}
                </p>
              </div>
              <button
                onClick={() => { setAnimationProgress(0); refetch(); }}
                className="btn btn-primary d-flex align-items-center gap-2"
                disabled={isLoadingSales}
                style={{ fontSize: '0.875rem', padding: '6px 14px' }}
              >
                {isLoadingSales
                  ? <Loader2 className="animate-spin" size={15} />
                  : <RefreshCw size={15} />}
                Refresh
              </button>
            </div>

            {isLoadingSales ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                <Loader2 className="animate-spin" size={48} />
              </div>
            ) : salesPerRep.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">No data available for the selected filters</p>
              </div>
            ) : (
              <>
                {/* ── Mobile tab switcher (visible only on xs/sm) ── */}
                <div className="d-flex d-lg-none mb-3 border rounded overflow-hidden" style={{ width: 'fit-content' }}>
                  <button
                    className={`btn btn-sm d-flex align-items-center gap-1 rounded-0 ${mobileTab === 'chart' ? 'btn-primary' : 'btn-light'}`}
                    style={{ fontSize: '0.8rem', padding: '6px 14px' }}
                    onClick={() => setMobileTab('chart')}
                  >
                    <BarChart2 size={14} /> Chart
                  </button>
                  <button
                    className={`btn btn-sm d-flex align-items-center gap-1 rounded-0 ${mobileTab === 'table' ? 'btn-primary' : 'btn-light'}`}
                    style={{ fontSize: '0.8rem', padding: '6px 14px' }}
                    onClick={() => setMobileTab('table')}
                  >
                    <List size={14} /> Table
                  </button>
                </div>

                {/* ── Desktop: side-by-side layout ── */}
                <Row className="g-3 d-none d-lg-flex">
                  <Col lg={4}>
                    <PerformanceTable />
                  </Col>
                  <Col lg={8}>
                    <LeaderboardChart />
                  </Col>
                </Row>

                {/* ── Tablet (md): stacked, both visible ── */}
                <div className="d-none d-md-flex d-lg-none flex-column gap-3">
                  <LeaderboardChart />
                  <PerformanceTable />
                </div>

                {/* ── Mobile: tab-switched ── */}
                <div className="d-md-none">
                  {mobileTab === 'chart' ? <LeaderboardChart /> : <PerformanceTable />}
                </div>
              </>
            )}
          </Row>
        </Col>
      </Row>

      <style>{`
        .card {
          box-shadow: 0 1px 4px rgba(0,0,0,.08);
          border: 1px solid rgba(0,0,0,.1);
          border-radius: 10px;
        }
        .table th {
          border-bottom: 2px solid #dee2e6;
          font-size: .75rem;
          text-transform: uppercase;
          letter-spacing: .05em;
          white-space: nowrap;
        }
        .table td {
          font-size: 0.875rem;
          vertical-align: middle;
        }
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Tighter padding on small screens */
        @media (max-width: 576px) {
          .card-header { padding: 10px 12px; }
          .card-body   { padding: 10px 12px; }
          .table td, .table th { padding: 8px 10px; }
        }
      `}</style>
    </Container>
  );
};

export default RacingTrackBoard;